import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill';
import flagFontUrl from 'country-flag-emoji-polyfill/dist/TwemojiCountryFlags.woff2?url';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import './index.css';
import { i18n } from '@/i18n';
import { LocaleProvider } from '@/i18n/LocaleProvider';
import { chunkRecoveryStarted, isChunkLoadError, registerChunkReloadListener } from '@/lib/chunk-reload';
import { queryClient } from '@/lib/query-client';
import { ThemeProvider } from '@/lib/theme';
import { installTranslatedDomGuard } from '@/lib/translated-dom-guard';

import App from './App.tsx';
import { AppErrorBoundary } from './AppErrorBoundary.tsx';

// Must run before the first render so a stale-chunk preload failure (e.g. on the
// very first lazy route navigation after a deploy) is caught.
registerChunkReloadListener();

installTranslatedDomGuard();

// Windows has no flag emoji and draws nationalities as letter pairs ("TH"); this loads a
// flags-only font there and nowhere else. Self-hosted, so it never reaches out to a CDN.
polyfillCountryFlagEmojis('Twemoji Country Flags', flagFontUrl);

const dsn = import.meta.env.VITE_SENTRY_DSN;
if (dsn) {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    // Sentry 11 collects user info, cookies, headers and query strings unless told not to.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { request: { allow: ['user-agent', 'content-type'] }, response: false },
      httpBodies: [],
      urlQueryParams: false,
    },
    beforeSend(event, hint) {
      // A stale chunk we reloaded out of is a deploy behaving normally and the user never
      // saw it. Only the ones we could not recover from are worth a report: those left
      // someone on the fallback screen, because the cooldown had already refused a reload.
      if (isChunkLoadError(hint?.originalException) && chunkRecoveryStarted()) return null;
      return event;
    },
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LocaleProvider>
          <AppErrorBoundary fallbackMessage={i18n.t('common.something_went_wrong')}>
            <App />
          </AppErrorBoundary>
        </LocaleProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>
);
